import * as React from 'react';
import { Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { cn } from '@shared';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

interface PasswordFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  error?: string;
  helperText?: string;
}

const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ id, label, error, helperText, className, 'aria-describedby': describedBy, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const messageId = error || helperText ? `${inputId}-message` : undefined;
    const [isVisible, setIsVisible] = React.useState(false);
    const descriptionIds = [describedBy, messageId].filter(Boolean).join(' ') || undefined;

    return (
      <div className="space-y-2">
        <Label htmlFor={inputId} className="text-sm font-medium text-foreground">
          {label}
        </Label>
        <div className="relative">
          <LockKeyhole
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            {...props}
            ref={ref}
            id={inputId}
            type={isVisible ? 'text' : 'password'}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={descriptionIds}
            className={cn(
              'h-12 rounded-xl border-input bg-background pl-10 pr-14 text-base sm:text-sm placeholder:text-muted-foreground focus-visible:ring-primary focus-visible:ring-offset-0',
              error && 'border-destructive focus-visible:ring-destructive',
              className,
            )}
          />
          <button
            type="button"
            aria-label={isVisible ? 'Hide password' : 'Show password'}
            aria-pressed={isVisible}
            aria-controls={inputId}
            title={isVisible ? 'Hide password' : 'Show password'}
            onClick={() => setIsVisible((visible) => !visible)}
            className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isVisible ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
        {(error || helperText) && (
          <p
            id={messageId}
            className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    );
  },
);

PasswordField.displayName = 'PasswordField';

export { PasswordField };
